import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useCombinedStore } from "../store";
import Modal from "react-modal";
import api from "../services/api.service";
import * as Styles from "../styles/TransferOrderDesk.Modal.styles";
import alfredoIcon from "../assets/alfredo-icon.svg";

interface TransferOrderCounterModalProps {
  isOpen: boolean;
  setSearchValueInput: (value: string) => void;
  onRequestClose: () => void;
  onNewOrder: () => void;
  availableCounters: any;
}

const TransferOrderCounterModal: React.FC<TransferOrderCounterModalProps> = ({
  isOpen,
  setSearchValueInput,
  onRequestClose,
  onNewOrder,
  availableCounters,
}) => {
  const {
    subsidiaryDataZustand,
    setOrderDataZustand,
    orderDataZustand,
    setMessageZustand,
  } = useCombinedStore((state) => {
    return {
      setSubsidiaryDataZustand: state.setSubsidiaryDataZustand,
      subsidiaryDataZustand: state.subsidiaryDataZustand,
      setOrderDataZustand: state.setOrderDataZustand,
      orderDataZustand: state.orderDataZustand,
      setMessageZustand: state.setMessageZustand,
    };
  });
  interface Counter {
    itemUuid: string;
    itemName: string;
    itemOrder: any;
  }
  const products = orderDataZustand.products ?? [];

  const selectedProducts: any =
    products?.filter(
      (product: any) => product.productSelected || product.productSemiSelected
    ) || [];

  const [selectedCounter, setSelectedCounter] = useState<Counter | null>(null);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedCounter = availableCounters.find(
      (counter: Counter) => counter.itemName === event.target.value
    );
    setSelectedCounter(selectedCounter || null);
    setSearchValueInput(event.target.value);
  };

  const handleSubmit = async () => {
    try {
      if (selectedCounter?.itemOrder) {
        for (const productOrder of selectedProducts) {
          api.post(`removeProductOrder/${orderDataZustand.orderUuid}`, {
            products: [
              {
                orderInfoUuid: productOrder.productOrderUuid,
                cancelReasonUuid: "00000000-0000-0000-0000-000000000000",
                productProduced: productOrder.productPrinted,
                observations: [], // Mesmo que não tenha observações, envie um array vazio
              },
            ],
          });
          api.post(
            `addProductOrder/${selectedCounter.itemOrder}`,
            {
              orderType: "PICKUP",
              productUuid: productOrder.productUuid,
              productPrinted: productOrder.productPrinted,
              productName: productOrder.productName,
              productPrice: Number(productOrder.productPrice.replace(",", ".")),
              productDiscount: productOrder.productDiscount,
              productCode: productOrder.productCode,
            }
          );
        }
        setMessageZustand(`Transfência realizada com sucesso!`, `success`);
        onRequestClose();
      } else {
        for (const productOrder of selectedProducts) {
          api.post(`removeProductOrder/${orderDataZustand.orderUuid}`, {
            products: [
              {
                orderInfoUuid: productOrder.productOrderUuid,
                cancelReasonUuid: "00000000-0000-0000-0000-000000000000",
                productProduced: productOrder.productPrinted,
                observations: [], // Mesmo que não tenha observações, envie um array vazio
              },
            ],
          });
        }
        setOrderDataZustand({
          ...orderDataZustand,
          productsTransfer: selectedProducts,
        });
        onNewOrder();
      }
    } catch (error: any) {
      if (error.message === "Network Error") {
        setMessageZustand(
          `Erro ao tentar transferir a conta, entre em contato com os administradores do Alfredo da sua unidade.`,
          `error`
        );
      } else {
        setMessageZustand(
          `Erro ao transferir a conta: ${error.message}`,
          `error`
        );
      }
    }
  };
  const modalWrapperRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        modalWrapperRef.current?.focus();
      }, 50);
    }
  }, [isOpen, setOrderDataZustand, orderDataZustand]);

  const sortCounters = (a: any, b: any) => {
    const numA = parseInt(a.itemName.replace(/\D/g, ""));
    const numB = parseInt(b.itemName.replace(/\D/g, ""));
    return numA - numB;
  };

  const mapCounters = (counter: any, index: number) => (
    <option key={index} value={counter.itemName}>
      {counter.itemName}
    </option>
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onRequestClose()}
      style={Styles.customStyles}
      contentLabel="Main Modal"
      ariaHideApp={false}
    >
      <Styles.ModalWrapper
        tabIndex={-1}
        ref={modalWrapperRef}
        onKeyDown={handleKeyDown}
      >
        <Styles.CloseButton onClick={() => onRequestClose()}>
          &times;
        </Styles.CloseButton>
        <Styles.ModalContent>
          <Styles.ModalHeader
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Logo src={alfredoIcon} alt="Alfredo Web OPS" />
            <Styles.ModalTitle>Transferir Conta</Styles.ModalTitle>
            <Styles.ModalDescription>
              Transfira a conta para outro balcão disponível.
            </Styles.ModalDescription>
          </Styles.ModalHeader>
          <Styles.ModalBody
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .backgroundColor
            }
          >
            <Styles.RowContainer>
              <Styles.ProductsTable>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                </tr>
                {selectedProducts.map((product: any, index: number) => (
                  <tr key={product.productOrderUuid}>
                    <td>{product.productCode}</td>
                    <td>{product.productName}</td>
                  </tr>
                ))}
              </Styles.ProductsTable>
            </Styles.RowContainer>
            <Styles.RowContainer>
              <Styles.FieldLabel>
                Escolha um balcão disponível
              </Styles.FieldLabel>
              <Styles.DropdownInput
                color={
                  subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                    .buttonsColor
                }
              >
                <select onChange={handleChange}>
                  <option value="">Selecione</option>
                  {availableCounters.sort(sortCounters).map(mapCounters)}
                </select>
                <label>Balcões disponíveis</label>
              </Styles.DropdownInput>
            </Styles.RowContainer>
          </Styles.ModalBody>
          <Styles.ModalFooter
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Button onClick={() => onRequestClose()}>
              Cancelar
            </Styles.Button>
            <Styles.Button onClick={handleSubmit}>Transferir</Styles.Button>
          </Styles.ModalFooter>
        </Styles.ModalContent>
      </Styles.ModalWrapper>
    </Modal>
  );
};

export default TransferOrderCounterModal;
